import React, { useState, useEffect, useCallback } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Snackbar,
  Chip,
  Grid,
  Card,
  CardContent,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Person as UserIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Close as CloseIcon,
  PersonAdd,
  Group as GroupIcon
} from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { User, UserInput, UserMapping, NotificationGroupMember, NotificationPreferencesInput, Role, Tenant } from '../types';
import { 
  GET_FILTERED_USERS
} from '../graphql/queries';
import { 
  CREATE_USER, 
  UPDATE_USER, 
  DELETE_USER
} from '../graphql/mutations';
import { useUserRole } from '../hooks/useUserRole';
import userService from '../services/userService';
import tenantService from '../services/tenantService';

const Users: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [filteredRoles, setFilteredRoles] = useState<Role[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const defaultNotificationPreferences: NotificationPreferencesInput = {
    enabled: false,
    smsEnabled: false,
    phoneNumber: '',
    signalREnabled: true,
  };

  const [formData, setFormData] = useState<UserInput>({
    username: '',
    emailAddress: '',
    firstName: '',
    lastName: '',
    displayName: '',
    languageCode: 'en-US',
    identityProvider: 'local',
    linkId: '',
    roleId: 1,
    mappings: [],
    notificationPreferences: { ...defaultNotificationPreferences },
    notificationGroups: [],
    isActive: true
  });

  // Notification groups search term (for adding members)
  const [ngSearchTerm, setNgSearchTerm] = useState<string>('');

  // Helper function to clean mappings data by removing __typename fields
  const cleanMappingsData = (mappings: any[]) => {
    return mappings.map(mapping => ({
      tenantId: mapping.tenantId,
      roleId: mapping.roleId
    }));
  };

  // Helper function to clean notification groups data by removing __typename fields
  const cleanNotificationGroupsData = (groups: any[]) => {
    return groups.map(member => ({
      userId: member.userId,
      status: member.status
    }));
  };

  const { user: currentUser, isAdmin: hasControlPanelAccess, isLoading: userLoading } = useUserRole();

  // GraphQL queries and mutations
  const { data: usersData, loading: usersLoading, refetch } = useQuery(GET_FILTERED_USERS);
  
  const [createUser, { loading: createLoading }] = useMutation(CREATE_USER, {
    onCompleted: () => {
      handleClose();
      setSnackbar({ open: true, message: 'User created successfully!', severity: 'success' });
      refetch();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: `Error creating user: ${error.message}`, severity: 'error' });
    }
  });

  const [updateUser, { loading: updateLoading }] = useMutation(UPDATE_USER, {
    onCompleted: () => {
      handleClose();
      setSnackbar({ open: true, message: 'User updated successfully!', severity: 'success' });
      refetch();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: `Error updating user: ${error.message}`, severity: 'error' });
    }
  });

  const [deleteUser, { loading: deleteLoading }] = useMutation(DELETE_USER, {
    onCompleted: () => {
      setSnackbar({ open: true, message: 'User deleted successfully!', severity: 'success' });
      refetch();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: `Error deleting user: ${error.message}`, severity: 'error' });
    }
  });

  const loadFilteredRoles = useCallback(async () => {
    if (!currentUser?.roleId) return;
    
    setLoadingRoles(true);
    try {
      // Backend now handles role filtering based on authenticated user
      const roles = await userService.getFilteredRoles();
      setFilteredRoles(roles || []); // Ensure we always set an array, never null
    } catch (error) {
      console.error('Error loading filtered roles:', error);
      setFilteredRoles([]); // Set empty array on error to prevent null map error
      setSnackbar({ open: true, message: 'Error loading roles', severity: 'error' });
    } finally {
      setLoadingRoles(false);
    }
  }, [currentUser?.roleId]);

  const loadTenants = useCallback(async () => {
    setLoadingTenants(true);
    try {
      const tenantsData = await tenantService.getTenants();
      setTenants(tenantsData);
    } catch (error) {
      console.error('Error loading tenants:', error);
      setSnackbar({ open: true, message: 'Error loading tenants', severity: 'error' });
    } finally {
      setLoadingTenants(false);
    }
  }, []);

  // Load filtered roles and tenants when current user is available
  useEffect(() => {
    if (currentUser?.roleId !== undefined) {
      loadFilteredRoles();
      loadTenants();
    }
  }, [currentUser, loadFilteredRoles, loadTenants]);

  const handleOpen = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setNgSearchTerm('');
      setFormData({
        username: user.username,
        emailAddress: user.emailAddress,
        firstName: user.firstName,
        lastName: user.lastName,
        displayName: user.displayName,
        languageCode: user.languageCode || 'en-US',
        identityProvider: user.identityProvider || 'local',
        linkId: user.linkId || '',
        roleId: user.roleId,
        mappings: user.mappings ? cleanMappingsData(user.mappings) : [],
        notificationPreferences: user.notificationPreferences ? {
          enabled: user.notificationPreferences.enabled,
          smsEnabled: user.notificationPreferences.smsEnabled,
          phoneNumber: user.notificationPreferences.phoneNumber || '',
          signalREnabled: user.notificationPreferences.signalREnabled,
        } : { ...defaultNotificationPreferences },
        notificationGroups: user.notificationGroups ? cleanNotificationGroupsData(user.notificationGroups) : [],
        isActive: user.isActive
      });
    } else {
      setEditingUser(null);
      setNgSearchTerm('');
      setFormData({
        username: '',
        emailAddress: '',
        firstName: '',
        lastName: '',
        displayName: '',
        languageCode: 'en-US',
        identityProvider: 'local',
        linkId: '',
        roleId: filteredRoles.length > 0 ? filteredRoles[0].roleId : 1,
        mappings: [],
        notificationPreferences: { ...defaultNotificationPreferences },
        notificationGroups: [],
        isActive: true
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingUser(null);
  };

  // Notification group helpers
  const addNgMember = (userId: string) => {
    const already = (formData.notificationGroups || []).some(m => m.userId === userId);
    if (already) return;
    setFormData(f => ({ ...f, notificationGroups: [...(f.notificationGroups || []), { userId, status: 1 }] }));
    setNgSearchTerm('');
  };

  const removeNgMember = (userId: string) => {
    setFormData(f => ({ ...f, notificationGroups: (f.notificationGroups || []).filter(m => m.userId !== userId) }));
  };

  const updateNgMemberStatus = (userId: string, status: number) => {
    setFormData(f => ({
      ...f,
      notificationGroups: (f.notificationGroups || []).map(m => m.userId === userId ? { ...m, status } : m)
    }));
  };

  const handleSubmit = () => {
    // Clean the form data by removing __typename fields from mappings
    const cleanedFormData = {
      ...formData,
      mappings: formData.mappings ? cleanMappingsData(formData.mappings) : [],
      notificationGroups: formData.notificationGroups ? cleanNotificationGroupsData(formData.notificationGroups) : [],
    };

    if (editingUser) {
      updateUser({
        variables: {
          id: editingUser.id,
          input: cleanedFormData
        }
      });
    } else {
      createUser({
        variables: {
          input: cleanedFormData
        }
      });
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      deleteUser({
        variables: { id }
      });
    }
  };

  const handleAddMapping = () => {
    if (!formData.mappings) {
      setFormData({ ...formData, mappings: [] });
    }
    
    setFormData({
      ...formData,
      mappings: [
        ...(formData.mappings || []),
        { tenantId: tenants.length > 0 ? tenants[0].id : '', roleId: filteredRoles.length > 0 ? filteredRoles[0].roleId : 1 }
      ]
    });
  };

  const handleRemoveMapping = (index: number) => {
    const newMappings = [...(formData.mappings || [])];
    newMappings.splice(index, 1);
    setFormData({ ...formData, mappings: newMappings });
  };

  const handleMappingChange = (index: number, field: keyof UserMapping, value: string | number) => {
    const newMappings = [...(formData.mappings || [])];
    newMappings[index] = { ...newMappings[index], [field]: value };
    setFormData({ ...formData, mappings: newMappings });
  };

  const getTenantName = (tenantId: string) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant?.name || 'Unknown';
  };

  if (userLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!hasControlPanelAccess) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You don't have permission to access the user management panel.
        </Alert>
      </Container>
    );
  }

  const users = usersData?.filteredUsers || [];
  const filteredUsers = users
    .filter((user: User) =>
      user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.emailAddress.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a: User, b: User) => a.displayName.localeCompare(b.displayName));

  const totalUsers = users.length;
  const activeUsers = users.filter((user: User) => user.isActive).length;
  const inactiveUsers = totalUsers - activeUsers;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
        User Management
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <UserIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Users
                  </Typography>
                  <Typography variant="h4">
                    {totalUsers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ActiveIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Users
                  </Typography>
                  <Typography variant="h4">
                    {activeUsers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <InactiveIcon sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Inactive Users
                  </Typography>
                  <Typography variant="h4">
                    {inactiveUsers}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Add Button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <TextField
          placeholder="Search users..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />
          }}
        />
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpen()}
          disabled={loadingRoles || loadingTenants}
        >
          Add User
        </Button>
      </Box>

      {/* Users Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Display Name</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Mappings</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usersLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No users found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user: User) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <UserIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {user.displayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.firstName} {user.lastName}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.emailAddress}</TableCell>
                    <TableCell>
                      <Chip
                        label={`Role ${user.roleId}`}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.mappings && user.mappings.length > 0 ? (
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          {user.mappings.map((mapping, index) => (
                            <Chip
                              key={index}
                              label={`${getTenantName(mapping.tenantId)} (Role ${mapping.roleId})`}
                              variant="outlined"
                              size="small"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No mappings
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={user.isActive ? <ActiveIcon /> : <InactiveIcon />}
                        label={user.isActive ? 'Active' : 'Inactive'}
                        color={user.isActive ? 'success' : 'error'}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpen(user)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(user.id)}
                          color="error"
                          disabled={deleteLoading}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit User Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email Address"
                type="email"
                value={formData.emailAddress}
                onChange={(e) => setFormData({ ...formData, emailAddress: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Display Name"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Language</InputLabel>
                <Select
                  value={formData.languageCode || 'en-US'}
                  onChange={(e) => setFormData({ ...formData, languageCode: e.target.value })}
                  label="Language"
                >
                  <MenuItem value="en-US">English (US)</MenuItem>
                  <MenuItem value="en-GB">English (UK)</MenuItem>
                  <MenuItem value="es-ES">Spanish</MenuItem>
                  <MenuItem value="fr-FR">French</MenuItem>
                  <MenuItem value="de-DE">German</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.roleId}
                  onChange={(e) => setFormData({ ...formData, roleId: e.target.value as number })}
                  label="Role"
                  disabled={loadingRoles}
                >
                  {(filteredRoles || []).map((role) => (
                    <MenuItem key={role.id} value={role.roleId}>
                      {role.name} (ID: {role.roleId})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Identity Provider"
                value={formData.identityProvider}
                onChange={(e) => setFormData({ ...formData, identityProvider: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Link ID"
                value={formData.linkId}
                onChange={(e) => setFormData({ ...formData, linkId: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive || false}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>

            {/* Notification Preferences Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>Notification Preferences</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notificationPreferences?.enabled || false}
                        onChange={(e) => setFormData({
                          ...formData,
                          notificationPreferences: {
                            ...formData.notificationPreferences!,
                            enabled: e.target.checked,
                          }
                        })}
                      />
                    }
                    label="Notifications Enabled"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notificationPreferences?.signalREnabled || false}
                        onChange={(e) => setFormData({
                          ...formData,
                          notificationPreferences: {
                            ...formData.notificationPreferences!,
                            signalREnabled: e.target.checked,
                          }
                        })}
                      />
                    }
                    label="Real-time (SignalR) Enabled"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.notificationPreferences?.smsEnabled || false}
                        onChange={(e) => setFormData({
                          ...formData,
                          notificationPreferences: {
                            ...formData.notificationPreferences!,
                            smsEnabled: e.target.checked,
                          }
                        })}
                      />
                    }
                    label="SMS Enabled"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone Number (E.164)"
                    placeholder="+15551234567"
                    value={formData.notificationPreferences?.phoneNumber || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      notificationPreferences: {
                        ...formData.notificationPreferences!,
                        phoneNumber: e.target.value,
                      }
                    })}
                    fullWidth
                    size="small"
                    disabled={!formData.notificationPreferences?.smsEnabled}
                    helperText="Required when SMS is enabled"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Notification Groups Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <GroupIcon color="primary" />
                <Typography variant="h6">Notification Groups (Caregivers)</Typography>
                <Chip label={`${(formData.notificationGroups || []).length} member(s)`} size="small" variant="outlined" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Caregivers who receive notifications about this user's activity. Search by name or email to add.
              </Typography>

              {/* Search to add */}
              <TextField
                label="Search users to add"
                value={ngSearchTerm}
                onChange={(e) => setNgSearchTerm(e.target.value)}
                fullWidth
                size="small"
                placeholder="Search by name, email, or username..."
                InputProps={{ startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} /> }}
                sx={{ mb: 1 }}
              />

              {/* Search results dropdown */}
              {ngSearchTerm.trim().length >= 2 && (() => {
                const term = ngSearchTerm.trim().toLowerCase();
                const currentMemberIds = new Set((formData.notificationGroups || []).map(m => m.userId));
                const results = users.filter((u: User) =>
                  u.id !== editingUser?.id &&
                  !currentMemberIds.has(u.id) &&
                  (
                    u.displayName?.toLowerCase().includes(term) ||
                    u.emailAddress?.toLowerCase().includes(term) ||
                    u.username?.toLowerCase().includes(term)
                  )
                ).slice(0, 8);
                return results.length > 0 ? (
                  <Paper variant="outlined" sx={{ mb: 2, maxHeight: 220, overflow: 'auto' }}>
                    <List dense disablePadding>
                      {results.map((u: User, idx: number) => (
                        <React.Fragment key={u.id}>
                          {idx > 0 && <Divider />}
                          <ListItem
                            secondaryAction={
                              <Tooltip title="Add to notification group">
                                <IconButton edge="end" size="small" color="primary" onClick={() => addNgMember(u.id)}>
                                  <PersonAdd fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            }
                          >
                            <ListItemText
                              primary={u.displayName}
                              secondary={`${u.emailAddress} · ${u.identityProvider}`}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  </Paper>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }}>No matching users found.</Alert>
                );
              })()}

              {/* Current members list */}
              {(formData.notificationGroups || []).length > 0 ? (
                <Paper variant="outlined">
                  <List dense disablePadding>
                    {(formData.notificationGroups || []).map((member, idx) => {
                      const memberUser = users.find((u: User) => u.id === member.userId);
                      const statusColor = member.status === 1 ? 'success' : member.status === 2 ? 'error' : 'warning';
                      const statusLabel = member.status === 1 ? 'Active' : member.status === 2 ? 'Rejected' : 'Pending';
                      return (
                        <React.Fragment key={member.userId}>
                          {idx > 0 && <Divider />}
                          <ListItem
                            secondaryAction={
                              <Box display="flex" alignItems="center" gap={1}>
                                <FormControl size="small" sx={{ minWidth: 105 }}>
                                  <Select
                                    value={member.status}
                                    onChange={(e) => updateNgMemberStatus(member.userId, e.target.value as number)}
                                    variant="outlined"
                                    size="small"
                                  >
                                    <MenuItem value={0}>Pending</MenuItem>
                                    <MenuItem value={1}>Active</MenuItem>
                                    <MenuItem value={2}>Rejected</MenuItem>
                                  </Select>
                                </FormControl>
                                <Tooltip title="Remove">
                                  <IconButton edge="end" size="small" color="error" onClick={() => removeNgMember(member.userId)}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            }
                          >
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <UserIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                  <Typography variant="body2" fontWeight="medium">
                                    {memberUser?.displayName || member.userId}
                                  </Typography>
                                  <Chip label={statusLabel} color={statusColor as any} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                                </Box>
                              }
                              secondary={memberUser ? `${memberUser.emailAddress} · ${memberUser.identityProvider}` : 'User ID: ' + member.userId}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Paper>
              ) : (
                <Box
                  sx={{
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    py: 3,
                    textAlign: 'center',
                  }}
                >
                  <GroupIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    No caregivers added. Search above to add members.
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* User Mappings Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6">User Mappings</Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddMapping}
                  disabled={loadingTenants || loadingRoles}
                >
                  Add Mapping
                </Button>
              </Box>
              
              {formData.mappings && formData.mappings.length > 0 ? (
                <List>
                  {formData.mappings.map((mapping, index) => (
                    <ListItem
                      key={index}
                      sx={{ border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveMapping(index)}
                          size="small"
                          color="error"
                        >
                          <CloseIcon />
                        </IconButton>
                      }
                    >
                      <Grid container spacing={2} alignItems="center" sx={{ flex: 1, pr: 5 }}>
                        <Grid item xs={6}>
                          <Autocomplete
                            size="small"
                            disabled={loadingTenants}
                            options={tenants}
                            getOptionLabel={(t) => t.name}
                            value={tenants.find(t => t.id === mapping.tenantId) ?? undefined}
                            onChange={(_, newVal) => handleMappingChange(index, 'tenantId', newVal?.id ?? '')}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                            disableClearable
                            renderInput={(params) => <TextField {...params} label="Tenant" />}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Autocomplete
                            size="small"
                            disabled={loadingRoles}
                            options={filteredRoles || []}
                            getOptionLabel={(r) => `${r.name} (ID: ${r.roleId})`}
                            value={(filteredRoles || []).find(r => r.roleId === mapping.roleId) ?? undefined}
                            onChange={(_, newVal) => handleMappingChange(index, 'roleId', newVal?.roleId ?? 0)}
                            isOptionEqualToValue={(opt, val) => opt.roleId === val.roleId}
                            disableClearable
                            renderInput={(params) => <TextField {...params} label="Role" />}
                          />
                        </Grid>
                      </Grid>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                  No mappings added yet. Click "Add Mapping" to assign this user to tenants.
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createLoading || updateLoading}
          >
            {createLoading || updateLoading ? (
              <CircularProgress size={20} />
            ) : editingUser ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Users;
